import PropertyGrid from "@/components/PropertyGrid";
import { useNavigate, useLocation } from "react-router-dom";
import { properties } from "@/data/properties";
import Header from "@/components/Header";
import { usePublishedProperties } from "@/hooks/usePublishedProperties";
import { getPriceAmount } from "@/utils/price";

const propertyTypeChoices = [
  { value: "", label: "Todos" },
  { value: "local", label: "Locales" },
  { value: "bodega", label: "Bodegas" },
  { value: "terreno", label: "Terrenos" },
  { value: "oficina", label: "Oficinas y consultorios" },
  { value: "casa", label: "Casas para negocio" },
];

const normalize = (text) =>
  (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-\s]+/g, " ")
    .trim();

const formatLocationName = (location = "") =>
  location
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getEconomicPropertyType = (property) => {
  const explicitType = normalize(
    property.tipoReal || property.tipo_real || property.realType || property.type
  );

  if (explicitType && !["economico", "economicos"].includes(explicitType)) {
    if (explicitType.includes("consultorio")) return "oficina";
    return explicitType;
  }

  const description = normalize(`${property.ribbon || ""} ${property.title || ""}`);
  if (description.includes("bodega")) return "bodega";
  if (description.includes("terreno")) return "terreno";
  if (description.includes("oficina") || description.includes("consultorio")) return "oficina";
  if (description.includes("casa")) return "casa";
  return "local";
};

const Economicos = () => {
  const navigate = useNavigate();
  const locationHook = useLocation();
  const publishedProperties = usePublishedProperties();
  const allProperties = [...publishedProperties, ...properties];
  const params = new URLSearchParams(locationHook.search);
  const selectedType = params.get("tipo") || "";

  const filters = {
    location: params.get("location") || "",
  };

const allEconomicos = allProperties.filter((p) => {
  const price = getPriceAmount(p);
  return price >= 2000 && price <= 8000;
});

// 🔥 FILTRAR ECONOMICOS + UBICACIÓN
const economicos = allProperties.filter((p) => {


  const price = getPriceAmount(p);

  // 🔥 SOLO ENTRE 2000 Y 8000
  if (price < 2000 || price > 8000) {
    return false;
  }

  if (selectedType && getEconomicPropertyType(p) !== selectedType) {
    return false;
  }

  // 📍 UBICACIÓN
if (filters.location) {
  const loc = normalize(`${p.address || ""} ${p.location || ""}`);
  const filtro = normalize(filters.location);

  if (!loc.includes(filtro) && !filtro.includes(loc)) {
    return false;
  }
}

  return true;

});

const hasLocationNoResults = Boolean(filters.location) && economicos.length === 0;
const locationName = formatLocationName(filters.location);

  return (
    
  <>

    <Header />

    <div></div>
    <div className="w-full pt-20">
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Volver al inicio"
        className="fixed right-4 top-20 z-40 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-black text-slate-600 shadow-sm"
      >
        X
      </button>

      {/* GRID */}
      <div className="max-w-7xl mx-auto px-4">

<section className="mb-7 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
  <div className="text-center">
    <h1 className="text-3xl font-black uppercase text-slate-950">Accesibles</h1>
    <p className="mt-1 font-bold uppercase text-slate-500">De $2,000 a $8,000</p>
  </div>
  <div className="mt-5 flex flex-wrap justify-center gap-2">
    {propertyTypeChoices.map(({ label, value }) => (
      <button
        key={value || "todos"}
        type="button"
        onClick={() => {
          const nextParams = new URLSearchParams(locationHook.search);
          if (value) nextParams.set("tipo", value);
          else nextParams.delete("tipo");
          navigate(`/economicos${nextParams.toString() ? `?${nextParams.toString()}` : ""}`);
        }}
        className={`rounded-full border px-4 py-2 text-sm font-black transition ${
          selectedType === value
            ? "border-orange-500 bg-orange-500 text-white"
            : "border-slate-200 bg-white text-slate-700 hover:border-orange-300 hover:text-orange-700"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
</section>

{hasLocationNoResults && (
  <div className="mb-5 rounded-2xl border border-orange-100 bg-orange-50 p-5 text-center shadow-sm">
    <h2 className="text-xl font-bold text-slate-950">
      Por el momento no hay accesibles en {locationName}
    </h2>
    <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-600">
      No encontramos propiedades de $2,000 a $8,000 en esta zona. Tenemos {allEconomicos.length} opciones accesibles en otras zonas de la ciudad.
    </p>
    <div className="mt-4 flex flex-col justify-center gap-3 sm:flex-row">
      <button
        type="button"
        onClick={() => navigate("/economicos")}
        className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
      >
        Ver todas las opciones de $2,000 a $8,000
      </button>
      <button
        type="button"
        onClick={() => navigate("/")}
        className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
      >
        Buscar otra zona
      </button>
    </div>
  </div>
)}

<PropertyGrid
  properties={economicos}
  loading={false}
  selectedCategory="economico"
  onlyEconomicos={true}
  showCategories={false}
  showRibbon={false}

  onCategoryChange={(category) => {
    const routes = {
      economico: "/economicos",
      local: "/locales",
      casa: "/casas",
      terreno: "/terrenos",
      bodega: "/bodegas"
    };

    navigate(routes[category] || "/");
  }}

  filters={filters}

  onFilterChange={(newFilters) =>
    setFilters(newFilters)
  }

  onPropertyClick={(property) =>
    navigate(`/propiedad/${property.id}`)
  }

  title=""
  subtitle=""
/>
      </div>

</div>

  </>
);
};

export default Economicos;
